import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithTokenHandling } from '../baseQuery';
import { buildSearchParamsAuto } from '../utils';
import { EXPORTS_TAG_TYPE } from './constants';

export interface Export {
  id: number;
  username: string;
  kind: 'VIEW' | 'TERRITORY_BULK';
  download_url: string | null;
  exported_url: string | null;
  file_format: 'csv' | 'xlsx';
  // Raw server value: string[], an object, or null. Narrow at the UI boundary.
  columns: unknown;
  status: 'SUCCESS' | 'PENDING' | 'FAILED';
  error_message: string | null;
  created_at: string;
  is_download_valid?: boolean;
  method: 'GET' | 'POST';
  body?: unknown;
}

export interface ExportsResponse {
  results?: Export[];
  count?: number;
  next?: string | null;
  previous?: string | null;
  [key: string]: unknown;
}

export interface GetExportsParams {
  q?: string;
  sort?: string;
  order?: string;
}

export interface RerunExportParams {
  id: number;
  fileName?: string;
}

export interface RerunExportResponse {
  message: string;
  export_id: number;
}

export const exportsApi = createApi({
  reducerPath: 'exportsApi',
  baseQuery: baseQueryWithTokenHandling,
  tagTypes: [EXPORTS_TAG_TYPE],
  endpoints: (builder) => ({
    getExports: builder.query<ExportsResponse, GetExportsParams | void>({
      query: (params) => {
        const searchParams = params
          ? buildSearchParamsAuto(params)
          : '';
        return {
          url: `v1/accounts/exports/${searchParams ? `?${searchParams}` : ''}`,
          method: 'GET',
        };
      },
      providesTags: [{ type: EXPORTS_TAG_TYPE, id: 'LIST' }],
    }),
    rerunExport: builder.mutation<RerunExportResponse, RerunExportParams>({
      query: ({ id, fileName }) => ({
        url: `v1/accounts/exports/${id}/rerun/`,
        method: 'POST',
        body: fileName ? { file_name: fileName } : {},
      }),
      invalidatesTags: [{ type: EXPORTS_TAG_TYPE, id: 'LIST' }],
    }),
  }),
});

export const { useGetExportsQuery, useRerunExportMutation } = exportsApi;
